import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react'
import { Button, Eyebrow, PrismLight, Segments, VisuallyHidden } from '@/components/ui'
import { identity as content } from '@/content/site'
import { useInView } from '@/hooks/useInView'
import { MEDIA, useMediaQuery } from '@/hooks/useMediaQuery'
import { useReducedMotion } from '@/hooks/useReducedMotion'
import { cx } from '@/lib/cx'
import { DURATION, EASE, gsap, SplitText, useGSAP } from '@/lib/gsap'
import { useQuality } from '@/lib/quality'
import { useAppStore } from '@/lib/store'
import { fill } from '@/lib/template'
import { withRetry } from '@/webgl/retry'
import {
  FONT_STRETCHES,
  LOCKUP_FONTS,
  MAX_NAME_LENGTH,
  deriveIdentity,
  seedHex,
  slugify,
  type Identity,
} from './identity'
import styles from './IdentityLab.module.css'
import type { IdentitySketch } from './sketch'

/** Waits for the OFL lockup fonts (and the subsets this name needs) before drawing. */
function loadLockupFonts(name: string): Promise<unknown> {
  if (typeof document === 'undefined' || !document.fonts) return Promise.resolve()
  const sample = name || 'Aa'
  return Promise.all([
    document.fonts.load(`600 48px "${LOCKUP_FONTS.anek}"`, sample),
    document.fonts.load(`500 12px "${LOCKUP_FONTS.mono}"`, `${sample} N°·0123456789ABCDEF`),
  ]).catch(() => undefined)
}

function typeLabel({ lockup }: Identity): string {
  const width = FONT_STRETCHES.find((s) => s.keyword === lockup.stretch)?.wdth ?? 100
  return lockup.family === 'mono'
    ? `${content.ui.fonts.mono} ${lockup.weight}`
    : `${content.ui.fonts.anek} ${lockup.weight} / ${width}`
}

/**
 * Identity Lab (`#identity`): a white-glass workspace around a live p5.js sketch. A shop
 * name becomes a deterministic identity board (mark, palette, pattern, lockup) that morphs
 * as you type. Presets, Shuffle (a new salt) and Download PNG. p5 loads lazily as the
 * section nears the viewport, pauses offscreen, and draws visitor text in OFL fonts only.
 */
export function IdentityLab() {
  const rootRef = useRef<HTMLElement>(null)
  const workspaceRef = useRef<HTMLDivElement>(null)
  const tiltRef = useRef<HTMLDivElement>(null)
  const hostRef = useRef<HTMLDivElement>(null)
  const sketchRef = useRef<IdentitySketch | null>(null)

  const [name, setName] = useState<string>(content.defaultName)
  const [salt, setSalt] = useState(0)
  const [ready, setReady] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [status, setStatus] = useState('')

  const reducedMotion = useReducedMotion()
  const introStarted = useAppStore((s) => s.phase !== 'boot')
  const menuOpen = useAppStore((s) => s.menuOpen)
  const finePointer = useMediaQuery(MEDIA.hover)
  const { dpr } = useQuality()
  const maxDpr = dpr[1]

  const identity = useMemo(() => deriveIdentity(name, salt, content.defaultName), [name, salt])
  const identityRef = useRef(identity)
  const optionsRef = useRef({ maxDpr, still: reducedMotion })

  const near = useInView(rootRef, { rootMargin: '120% 0px', once: true })
  const visible = useInView(hostRef, { rootMargin: '15% 0px' })

  // Lazy-load p5 and mount the sketch once the section is near.
  useEffect(() => {
    const host = hostRef.current
    if (!near || !host) return
    let cancelled = false
    let sketch: IdentitySketch | null = null

    void withRetry(() =>
      Promise.all([import('p5'), import('./sketch'), loadLockupFonts(identityRef.current.name)]),
    )
      .then(([{ default: P5 }, { createIdentitySketch }]) => {
        if (cancelled) return
        sketch = createIdentitySketch(P5, host, identityRef.current, optionsRef.current)
        sketchRef.current = sketch
        setReady(true)
      })
      .catch((error: unknown) => {
        if (!cancelled) console.warn('[identity] sketch failed to load', error)
      })

    return () => {
      cancelled = true
      sketch?.destroy()
      sketchRef.current = null
      setReady(false)
    }
  }, [near])

  // Morph to each new identity; redraw once its font subsets arrive (e.g. Devanagari).
  useEffect(() => {
    identityRef.current = identity
    const sketch = sketchRef.current
    if (!sketch) return
    sketch.setIdentity(identity)
    let live = true
    void loadLockupFonts(identity.name).then(() => {
      if (live) sketchRef.current?.refresh()
    })
    return () => {
      live = false
    }
  }, [identity, ready])

  // Announce the settled identity politely, not on every keystroke.
  useEffect(() => {
    if (!ready) return
    const timer = window.setTimeout(
      () => setStatus(fill(content.ui.status, { name: identity.name })),
      900,
    )
    return () => window.clearTimeout(timer)
  }, [identity, ready])

  useEffect(() => {
    optionsRef.current = { maxDpr, still: reducedMotion }
    sketchRef.current?.setOptions({ maxDpr, still: reducedMotion })
  }, [maxDpr, reducedMotion, ready])

  useEffect(() => {
    sketchRef.current?.setActive(visible && !menuOpen)
  }, [visible, menuOpen, ready])

  // Header reveal, plus a gentle, damped tilt of the board toward a fine pointer.
  useGSAP(
    () => {
      if (!introStarted) return
      const root = rootRef.current
      if (!root) return
      const reveal = root.querySelectorAll('[data-reveal]')
      if (reducedMotion) {
        gsap.from(reveal, {
          autoAlpha: 0,
          duration: 0.3,
          scrollTrigger: { trigger: root, start: 'top 80%', once: true },
        })
        return
      }
      const title = root.querySelector('h2')
      if (title) {
        SplitText.create(title, {
          type: 'lines',
          mask: 'lines',
          autoSplit: true,
          onSplit: (self) =>
            gsap.from(self.lines, {
              yPercent: 105,
              stagger: 0.08,
              duration: DURATION.cinematic,
              scrollTrigger: { trigger: title, start: 'top 82%', once: true },
            }),
        })
      }
      gsap.from(reveal, {
        y: 28,
        autoAlpha: 0,
        stagger: 0.08,
        duration: DURATION.cinematic,
        scrollTrigger: { trigger: root, start: 'top 72%', once: true },
      })
      const workspace = workspaceRef.current
      if (workspace) {
        // Keep the input and actions in the tab order before their scroll reveal.
        // No scale: scaling this big glass panel re-rasterises it (shadows and all) on every
        // frame of the reveal, which is what froze the scroll just after the shop.
        gsap.from(workspace, {
          y: 64,
          opacity: 0,
          duration: DURATION.cinematic * 1.2,
          scrollTrigger: { trigger: workspace, start: 'top 88%', once: true },
        })
      }
    },
    { scope: rootRef, dependencies: [introStarted, reducedMotion] },
  )

  useGSAP(
    () => {
      const workspace = workspaceRef.current
      const tilt = tiltRef.current
      if (!workspace || !tilt || reducedMotion || !finePointer) return
      const rx = gsap.quickTo(tilt, 'rotationX', { duration: 1.6, ease: EASE.out })
      const ry = gsap.quickTo(tilt, 'rotationY', { duration: 1.6, ease: EASE.out })
      const onMove = (event: PointerEvent) => {
        const rect = tilt.getBoundingClientRect()
        const nx = (event.clientX - (rect.left + rect.width / 2)) / rect.width
        const ny = (event.clientY - (rect.top + rect.height / 2)) / rect.height
        ry(gsap.utils.clamp(-1, 1, nx) * 4)
        rx(gsap.utils.clamp(-1, 1, ny) * -3)
      }
      const onLeave = () => {
        rx(0)
        ry(0)
      }
      workspace.addEventListener('pointermove', onMove)
      workspace.addEventListener('pointerleave', onLeave)
      return () => {
        workspace.removeEventListener('pointermove', onMove)
        workspace.removeEventListener('pointerleave', onLeave)
      }
    },
    { dependencies: [reducedMotion, finePointer], revertOnUpdate: true },
  )

  const onName = (event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value)
    setSalt(0)
  }

  const download = async () => {
    const sketch = sketchRef.current
    if (!sketch || exporting) return
    setExporting(true)
    try {
      const blob = await sketch.exportPNG()
      if (!blob) return
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      const suffix = identity.salt ? `-${identity.salt}` : ''
      link.href = url
      link.download = `${slugify(identity.name)}-${content.ui.fileSuffix}${suffix}.png`
      document.body.append(link)
      link.click()
      link.remove()
      window.setTimeout(() => URL.revokeObjectURL(url), 2000)
    } finally {
      setExporting(false)
    }
  }

  const hex = seedHex(identity.seed)
  const swatches = [
    identity.colors.brand,
    identity.colors.accent,
    identity.colors.tint,
    identity.colors.tint2,
    identity.colors.ink,
  ]
  const patternName = content.ui.patterns[identity.pattern.kind]
  const boardLabel = fill(content.ui.boardLabel, {
    name: identity.name,
    pattern: patternName.toLowerCase(),
  })

  return (
    <section
      ref={rootRef}
      id="identity"
      aria-labelledby="identity-title"
      className={styles.root}
      data-theme="light"
    >
      <div className={cx('container', styles.inner)}>
        <header className={styles.header}>
          <Eyebrow data-reveal>{content.eyebrow}</Eyebrow>
          <h2 id="identity-title" className={styles.title}>
            <Segments segments={content.title} />
          </h2>
          <p className={styles.lead} data-reveal>
            {content.lead}
          </p>
        </header>

        <div className={styles.lab}>
          <PrismLight className={styles.light} placement="center" intensity={0.85} />
          <div ref={workspaceRef} className={styles.workspace}>
            <div className={styles.controls}>
              <div className={styles.field}>
                <label htmlFor="identity-name" className={styles.label}>
                  {content.ui.inputLabel}
                </label>
                <input
                  id="identity-name"
                  className={styles.input}
                  type="text"
                  value={name}
                  onChange={onName}
                  placeholder={content.placeholder}
                  maxLength={MAX_NAME_LENGTH}
                  autoComplete="off"
                  autoCapitalize="words"
                  spellCheck={false}
                  enterKeyHint="done"
                  data-cursor="text"
                />
              </div>

              <div className={styles.chips} role="group" aria-label={content.ui.presetsLabel}>
                {content.presets.map((preset) => {
                  const active = salt === 0 && name.trim().toLowerCase() === preset.toLowerCase()
                  return (
                    <button
                      key={preset}
                      type="button"
                      className={cx(styles.chip, active && styles.chipActive)}
                      aria-pressed={active}
                      data-cursor="link"
                      onClick={() => {
                        setName(preset)
                        setSalt(0)
                      }}
                    >
                      {preset}
                    </button>
                  )
                })}
              </div>

              <div className={styles.actions}>
                <Button
                  variant="accent"
                  icon="shuffle"
                  className={styles.action}
                  onClick={() => setSalt((value) => value + 1)}
                >
                  {content.actions.shuffle}
                </Button>
                <Button
                  variant="primary"
                  icon="download"
                  className={styles.action}
                  onClick={() => void download()}
                  disabled={!ready || exporting}
                  aria-busy={exporting || undefined}
                >
                  {content.actions.download}
                </Button>
              </div>

              <dl className={styles.readout}>
                <div className={styles.readoutItem}>
                  <dt>{content.ui.readout.seed}</dt>
                  <dd className="tabular">
                    {hex.slice(0, 4)}·{hex.slice(4)}
                  </dd>
                </div>
                <div className={styles.readoutItem}>
                  <dt>{content.ui.readout.pattern}</dt>
                  <dd>{patternName}</dd>
                </div>
                <div className={cx(styles.readoutItem, styles.readoutType)}>
                  <dt>{content.ui.readout.type}</dt>
                  <dd>{typeLabel(identity)}</dd>
                </div>
                <div className={styles.readoutItem}>
                  <dt>{content.ui.readout.palette}</dt>
                  <dd className={styles.swatches}>
                    {swatches.map((color, index) => (
                      <span
                        key={index}
                        className={styles.swatch}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                    <VisuallyHidden>{swatches.join(', ')}</VisuallyHidden>
                  </dd>
                </div>
              </dl>

              <p className={styles.note}>{content.note}</p>
            </div>

            <div className={styles.stage}>
              <div ref={tiltRef} className={styles.tilt}>
                <div className={styles.glow} aria-hidden="true" />
                <div
                  ref={hostRef}
                  className={cx(styles.board, ready && styles.boardReady)}
                  role="img"
                  aria-label={boardLabel}
                >
                  <div className={styles.placeholder} aria-hidden="true">
                    <span className={styles.placeholderMark} />
                    <span className={styles.placeholderLine} />
                    <span className={styles.placeholderBand} />
                  </div>
                </div>
              </div>
            </div>

            <p className={styles.status} role="status">
              {status}
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}
